import { ViewBooking } from "collins-js-sdk";
import React, { useContext, useState } from "react";
import { selectedDateBookingList } from "../../../../APIs/Booking/GetBookings";
import { DiaryContext } from "../../../../context/DiaryContext";
import {
  copyDayBookings,
  CopyDayProgress,
  generateGroundhogDayBookings,
  GenerationProgress
} from "../../../../services/groundhogDay";
import { EDSButton } from "../../../eds/EDSButton";
import { EDSDatePicker } from "../../../eds/EDSDatePicker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../ui/dialog";

type GenerationMode = "groundhog" | "copy";

const MAX_DATE_RANGE_DAYS = 30;

const calculateDaysBetween = (start: Date, end: Date): number => {
  const startTime = new Date(start).setHours(0, 0, 0, 0);
  const endTime = new Date(end).setHours(0, 0, 0, 0);
  const diffTime = endTime - startTime;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
};

interface GroundhogDayModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface GenerationOptionsState {
  randomiseStates: boolean;
  includeDeposits: boolean;
  includePreorders: boolean;
  includeDietaryRequirements: boolean;
  includeNotes: boolean;
  includeComments: boolean;
}

export const GroundhogDayModal: React.FC<GroundhogDayModalProps> = ({
  isOpen,
  onClose
}) => {
  const diaryContext = useContext(DiaryContext);

  const [mode, setMode] = useState<GenerationMode>("groundhog");
  const [selectedVenueId, setSelectedVenueId] = useState<string>(
    diaryContext.selectedVenue ?? ""
  );
  const [sourceDate, setSourceDate] = useState<Date | undefined>(undefined);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<
    GenerationProgress | CopyDayProgress | null
  >(null);
  const [result, setResult] = useState<{
    success: boolean;
    bookingsCreated: number;
    errors: string[];
  } | null>(null);

  const [options, setOptions] = useState<GenerationOptionsState>({
    randomiseStates: true,
    includeDeposits: true,
    includePreorders: true,
    includeDietaryRequirements: true,
    includeNotes: true,
    includeComments: true
  });

  const venues = diaryContext.venues ?? [];

  const handleGenerate = async () => {
    if (!startDate || !endDate) {
      return;
    }

    if (mode === "copy" && !sourceDate) {
      return;
    }

    setIsGenerating(true);
    setResult(null);
    setProgress(null);

    try {
      if (mode === "groundhog") {
        const generationResult = await generateGroundhogDayBookings(
          diaryContext,
          startDate,
          endDate,
          options,
          (p) => setProgress(p)
        );
        setResult(generationResult);
      } else {
        const sourceBookings = await fetchSourceDayBookings();
        if (!sourceBookings || sourceBookings.length === 0) {
          setResult({
            success: false,
            bookingsCreated: 0,
            errors: ["No bookings found on source day to copy"]
          });
          setIsGenerating(false);
          return;
        }

        const copyResult = await copyDayBookings(
          sourceBookings,
          diaryContext,
          startDate,
          endDate,
          options,
          (p) => setProgress(p)
        );
        setResult(copyResult);
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      setResult({
        success: false,
        bookingsCreated: 0,
        errors: [errorMessage]
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const fetchSourceDayBookings = async (): Promise<ViewBooking[]> => {
    if (!sourceDate || !selectedVenueId) {
      return [];
    }

    try {
      const bookings = await selectedDateBookingList(
        sourceDate,
        selectedVenueId
      );
      return bookings ?? [];
    } catch {
      return [];
    }
  };

  const handleOptionChange = (key: keyof GenerationOptionsState) => {
    setOptions((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleClose = () => {
    if (!isGenerating) {
      setResult(null);
      setProgress(null);
      onClose();
    }
  };

  const getDateRangeValidation = (): {
    isValid: boolean;
    error: string | null;
  } => {
    if (!startDate || !endDate) {
      return { isValid: false, error: null };
    }

    const daysBetween = calculateDaysBetween(startDate, endDate);

    if (daysBetween < 1) {
      return {
        isValid: false,
        error: "End date must be on or after start date"
      };
    }

    if (daysBetween > MAX_DATE_RANGE_DAYS) {
      return {
        isValid: false,
        error: `Date range cannot exceed ${MAX_DATE_RANGE_DAYS} days. Selected range: ${daysBetween} days`
      };
    }

    return { isValid: true, error: null };
  };

  const dateRangeValidation = getDateRangeValidation();

  const isFormValid =
    selectedVenueId &&
    startDate &&
    endDate &&
    dateRangeValidation.isValid &&
    (mode === "groundhog" || sourceDate);

  const getProgressStatusText = (
    status: GenerationProgress["status"] | CopyDayProgress["status"]
  ): string => {
    if (status === "generating" || status === "copying") {
      return `Processing ${progress?.currentDate}...`;
    }
    if (status === "complete") {
      return "Complete!";
    }
    if (status === "error") {
      return "Completed with errors";
    }
    return "Preparing...";
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Test Data Generator</DialogTitle>
          <DialogDescription>
            Generate test booking data for development and testing purposes.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            <button
              type="button"
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === "groundhog"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
              onClick={() => setMode("groundhog")}
              disabled={isGenerating}
            >
              Groundhog Day
            </button>
            <button
              type="button"
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                mode === "copy"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/80"
              }`}
              onClick={() => setMode("copy")}
              disabled={isGenerating}
            >
              Copy Day
            </button>
          </div>

          <p className="text-sm text-muted-foreground">
            {mode === "groundhog"
              ? "Generate bookings using predefined template guests with comprehensive data (dietary requirements, deposits, preorders, notes, etc.)."
              : "Copy all bookings from a source day to the target date range."}
          </p>

          <div className="flex flex-col gap-3">
            <div>
              <label
                htmlFor="venue-select"
                className="block text-sm font-medium mb-1"
              >
                Site
              </label>
              <select
                id="venue-select"
                className="w-full px-3 py-2 rounded-md border bg-background"
                value={selectedVenueId}
                onChange={(e) => setSelectedVenueId(e.target.value)}
                disabled={isGenerating}
              >
                <option value="">Select a site</option>
                {venues.map((venue) => (
                  <option key={venue._id} value={venue._id}>
                    {venue.title}
                  </option>
                ))}
              </select>
            </div>

            {mode === "copy" && (
              <div>
                <label
                  htmlFor="source-date-picker"
                  className="block text-sm font-medium mb-1"
                >
                  Source Date
                </label>
                <EDSDatePicker
                  id="source-date-picker"
                  date={sourceDate}
                  onSelect={setSourceDate}
                  placeholder="Select source date"
                  disabled={isGenerating}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor="start-date-picker"
                  className="block text-sm font-medium mb-1"
                >
                  Start Date
                </label>
                <EDSDatePicker
                  id="start-date-picker"
                  date={startDate}
                  onSelect={setStartDate}
                  placeholder="Select start date"
                  disabled={isGenerating}
                />
              </div>
              <div>
                <label
                  htmlFor="end-date-picker"
                  className="block text-sm font-medium mb-1"
                >
                  End Date
                </label>
                <EDSDatePicker
                  id="end-date-picker"
                  date={endDate}
                  onSelect={setEndDate}
                  placeholder="Select end date"
                  disabled={isGenerating}
                />
              </div>
            </div>

            {dateRangeValidation.error && (
              <p
                className="text-sm text-red-600 dark:text-red-400"
                role="alert"
              >
                {dateRangeValidation.error}
              </p>
            )}
          </div>

          <div className="border-t pt-3">
            <p className="text-sm font-medium mb-2">Options</p>
            <div className="grid grid-cols-2 gap-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={options.randomiseStates}
                  onChange={() => handleOptionChange("randomiseStates")}
                  disabled={isGenerating}
                  className="rounded"
                />
                <span>Randomise states</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={options.includeDeposits}
                  onChange={() => handleOptionChange("includeDeposits")}
                  disabled={isGenerating}
                  className="rounded"
                />
                <span>Include deposits</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={options.includePreorders}
                  onChange={() => handleOptionChange("includePreorders")}
                  disabled={isGenerating}
                  className="rounded"
                />
                <span>Include preorders</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={options.includeDietaryRequirements}
                  onChange={() =>
                    handleOptionChange("includeDietaryRequirements")
                  }
                  disabled={isGenerating}
                  className="rounded"
                />
                <span>Include dietary</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={options.includeNotes}
                  onChange={() => handleOptionChange("includeNotes")}
                  disabled={isGenerating}
                  className="rounded"
                />
                <span>Include notes</span>
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={options.includeComments}
                  onChange={() => handleOptionChange("includeComments")}
                  disabled={isGenerating}
                  className="rounded"
                />
                <span>Include comments</span>
              </label>
            </div>
          </div>

          {progress && (
            <div className="bg-muted p-3 rounded-md">
              <div className="flex justify-between text-sm mb-1">
                <span>{getProgressStatusText(progress.status)}</span>
                <span>
                  {progress.current} / {progress.total}
                </span>
              </div>
              <div className="w-full bg-background rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{
                    width: `${(progress.current / progress.total) * 100}%`
                  }}
                />
              </div>
              {"currentGuest" in progress && progress.currentGuest && (
                <p className="text-xs text-muted-foreground mt-1">
                  {progress.currentGuest}
                </p>
              )}
              {"currentBooking" in progress && progress.currentBooking && (
                <p className="text-xs text-muted-foreground mt-1">
                  {progress.currentBooking}
                </p>
              )}
            </div>
          )}

          {result && (
            <div
              className={`p-3 rounded-md ${
                result.success
                  ? "bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-200"
                  : "bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-200"
              }`}
            >
              <p className="font-medium">
                {result.success
                  ? `Successfully created ${result.bookingsCreated} bookings`
                  : `Created ${result.bookingsCreated} bookings with ${result.errors.length} errors`}
              </p>
              {result.errors.length > 0 && (
                <ul className="text-sm mt-2 list-disc list-inside max-h-32 overflow-y-auto">
                  {result.errors.slice(0, 10).map((error) => (
                    <li key={error}>{error}</li>
                  ))}
                  {result.errors.length > 10 && (
                    <li>...and {result.errors.length - 10} more errors</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <EDSButton
            variant="secondary"
            onClick={handleClose}
            disabled={isGenerating}
          >
            {result ? "Close" : "Cancel"}
          </EDSButton>
          {!result && (
            <EDSButton
              variant="default"
              onClick={handleGenerate}
              disabled={!isFormValid || isGenerating}
            >
              {isGenerating ? "Generating..." : "Generate"}
            </EDSButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default GroundhogDayModal;
